import { useEffect, useState } from 'react'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { Code } from '@connectrpc/connect'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { DialogClose } from '@radix-ui/react-dialog'
import { useMutation } from '@tanstack/react-query'

import type { AppError } from '@jotta/types/AppError'
import { Box } from '@jotta/ui/Box'
import { Button } from '@jotta/ui/Button'
import { Label } from '@jotta/ui/Label'
import { RadixDialog } from '@jotta/ui/RadixDialog'
import type { RadixModalProps } from '@jotta/ui/RadixModal'
import { RadixModal } from '@jotta/ui/RadixModal'
import { TextInput } from '@jotta/ui/TextInput'

import {
  nameForServer,
  normalizePathItemName,
} from '../../PathItem/normalizePathItemName'
import { validatePath } from '../../PathItem/validatePath'
import type { FileActionStore } from '../FileActionStore'
const debug = Debug('jotta:files:action:RenameDialog')

type CreateFolderDialogProps = {
  createFolder: FileActionStore['createFolder']
  error: AppError | null
  clearError: (open?: boolean) => void
  initialName?: string
} & RadixModalProps

export const CreateFolderDialog = observer<CreateFolderDialogProps>(
  function CreateFolderDialog({
    error,
    clearError,
    open = false,
    createFolder,
    onClose,
    initialName = '',
    ...props
  }) {
    if (error) {
      debug('error', error, error.message, error.grpcStatus)
    }
    const [newName, setNewName] = useState(initialName)
    const [errorMessage, setErrorMessage] = useState('')
    const validate = (name: string) => {
      // Validate the trimmed name, so whitespace-only reads as missing
      const pathError = validatePath(nameForServer(name))
      setErrorMessage(pathError ? pathError : '')
    }

    const createFolderMutation = useMutation({ mutationFn: createFolder })

    useEffect(() => {
      if (error) {
        setErrorMessage(getFolderErrorMessage(error))
        const timer = setTimeout(() => {
          setErrorMessage('')
        }, 5000)
        return () => clearTimeout(timer)
      }
    }, [error])

    return (
      <RadixModal open={open} onClose={onClose} {...props}>
        <RadixDialog
          data-testid="CreateFolderDialog"
          title={t`Create a new folder`}
          onClose={onClose}
          customButtons={
            <Box variant="dialogs.footer">
              <Button
                disabled={Boolean(errorMessage)}
                className="btn btn-primary"
                onClick={() =>
                  createFolderMutation.mutateAsync(nameForServer(newName))
                }
              >
                <Trans>Create</Trans>
              </Button>
              <DialogClose className="btn btn-outline">
                <Trans>Close</Trans>
              </DialogClose>
            </Box>
          }
        >
          <form
            onSubmit={e => {
              e.preventDefault()
              if (!errorMessage) {
                createFolderMutation.mutateAsync(nameForServer(newName))
              }
            }}
          >
            <Label
              label={t`Name`}
              errorMessage={
                errorMessage && (
                  <span data-testid="CreateFolderError">{errorMessage}</span>
                )
              }
            >
              <TextInput
                ref={focusRef}
                id="createfolder"
                value={newName}
                placeholder={t`untitled folder`}
                onChange={e => {
                  const name = normalizePathItemName(e.target.value)
                  setNewName(name)
                  validate(name)
                }}
              />
            </Label>
          </form>
        </RadixDialog>
      </RadixModal>
    )
  },
)
function focusRef(el: HTMLInputElement | null) {
  if (el) {
    el.focus()
  }
}

export function getCreateFolderErrorMessageForStatusCode(status?: Code) {
  if (!status) {
    return ''
  }
  switch (status) {
    case Code.AlreadyExists:
      return t`There is already a file or folder with this name, the operation was aborted.`

    default:
      return t`Unknown error please try again later.`
  }
}
export function getFolderErrorMessage(error: AppError | null) {
  if (!error) {
    return ''
  }
  return getCreateFolderErrorMessageForStatusCode(error.grpcStatus)
}
